import { BookingRequestDetails } from "redux/reducers/Properties/PropertiesSliceTypes";

export type BookingStatus =
  | "booking_request"
  | "invoice_sent"
  | "payment_received"
  | "confirmed"
  | "canceled";

/** Backend may still send the legacy "invitation_sent"; treat it as a plain request. */
export const normalizeStatus = (status?: string | null): BookingStatus => {
  switch (status) {
    case "invoice_sent":
    case "payment_received":
    case "confirmed":
    case "canceled":
    case "booking_request":
      return status;
    case "cancelled":
      return "canceled";
    default:
      return "booking_request"; // includes "invitation_sent"
  }
};

export interface StepDef {
  status: Exclude<BookingStatus, "canceled">;
  label: string;
}

// Same four steps as BookingProgressTracker, with short labels.
export const STEPS: StepDef[] = [
  { status: "booking_request", label: "Booking request" },
  { status: "invoice_sent", label: "Invoice" },
  { status: "payment_received", label: "Payment" },
  { status: "confirmed", label: "Confirmed" },
];

export type StepState = "done" | "current" | "upcoming";

/**
 * "You are here" = the next thing that still has to happen.
 * booking_request -> invoice is next, invoice_sent -> payment is next,
 * payment_received -> confirmation is next, confirmed -> everything done.
 * (The old BookingProgressTracker highlighted the step the status names instead.)
 */
export const getStepStates = (status: BookingStatus): StepState[] => {
  if (status === "confirmed") return STEPS.map(() => "done");
  const doneCount = STEPS.findIndex((s) => s.status === status) + 1;
  return STEPS.map((_, i) =>
    i < doneCount ? "done" : i === doneCount ? "current" : "upcoming"
  );
};

/**
 * One place to decide "how much does the guest owe". The old page showed
 * `total_amount` but PayPal charged `reserveBooking.booking.total`.
 * TODO: confirm with backend which field is authoritative and use it for PayPal too.
 */
export const getAmountDue = (b?: BookingRequestDetails | null): number => {
  if (!b) return 0;
  const n = Number(b.total_amount ?? b.invoice?.total ?? b.total);
  return Number.isFinite(n) ? n : 0;
};

/** Contact details and the full address are only for confirmed bookings. */
export const canSeeContact = (status: BookingStatus) => status === "confirmed";

export const firstName = (name?: string | null) =>
  name ? String(name).trim().split(/\s+/)[0] : "";
